/* eslint-disable prettier/prettier */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { DashboardContext } from 'components/screens/PatientDashboard/context';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { MedicationNonUseDto } from '@peartherapeutics/hylia-api-client';
import moment, { Moment } from 'moment';
import { DATE_FORMATS } from 'utils/dateTime';
import BuprenorphineNonUse from './BuprenorphineNonUse';
import StatusBar from './StatusBar';
import LegendBar from './LegendBar';
import styles from './styles.module.scss';
import SubstanceUseCalendar from './SubstanceUse';
import { calendarService } from './services';
import { IBupNonUse } from './selectors';

const Calendar = () => {
  // ==== STATE ==== //
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [bupNonUses, setBupNonUses] = useState<IBupNonUse[]>([]);
  const [showBup, setShowBup] = useState<boolean>(false);

  // ==== CONTEXT ==== //
  const { usageReport, setUsageReport, patientId, selectedAccessRight } =
    useContext(DashboardContext);

  // ===== METHODS ==== //
  const getUsageReport = useCallback(async () => {
    try {
      const report = await calendarService.getPatientUsageReport(
        patientId!,
        selectedAccessRight!.id,
      );

      setUsageReport({
        ...report,
      });

      setIsLoaded(true);
    } catch (e) {
      ErrorInterceptor.error(e);
    }
  }, [patientId, selectedAccessRight, setUsageReport]);

  const updateMedicationCompliances = (calendarMonth: Moment) => {
    const nonUses = usageReport!.medicationNonUses.filter(
      (nonUse: MedicationNonUseDto) => {
        const nonUseDate = moment(nonUse.date).format(DATE_FORMATS.MMYYYY);
        const calendarDate = calendarMonth.format(DATE_FORMATS.MMYYYY);
        return nonUseDate === calendarDate;
      },
    );
    const medicationCompliance = usageReport!.dailySummaries
      .filter((ds) => {
        const useDate = moment(ds.date).format(DATE_FORMATS.MMYYYY);
        const calendarDate = calendarMonth.format(DATE_FORMATS.MMYYYY);
        return useDate === calendarDate;
      })
      .some((ds) => ds.medicationCompliance !== undefined);
    setShowBup(medicationCompliance);
    setBupNonUses(nonUses);
  };

  useEffect(() => {
    if (!usageReport) {
      getUsageReport();
    } else {
      setIsLoaded(true);
    }
  }, [getUsageReport, usageReport]);

  if (isLoaded && usageReport) {
    return (
      <div id="calendar-tab-id" data-testid={'calendar-loaded'}>
        <StatusBar
          totalDaysUsed={usageReport!.substanceUseDaysCount}
          daysSubstanceUsedThisMonth={
            usageReport!.substanceUseDaysCountThisMonth
          }
          daysBupUsedThisMonth={usageReport!.medicationUseDaysCountThisMonth}
          prescriptionStatus={selectedAccessRight!.status}
        />
        <div className={styles['module-container']}>
          <LegendBar showBup={showBup} />
          <SubstanceUseCalendar
            updateMedicationCompliances={updateMedicationCompliances}
            dailySummaries={usageReport!.dailySummaries}
            showBup={showBup}
          />
        </div>
        {bupNonUses.length > 0 && (
          <BuprenorphineNonUse bupNonUses={bupNonUses} />
        )}
      </div>
    );
  }

  return <div data-testid={'calendar-loading'} />;
};

export default Calendar;
